import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import styles from './Breadcrumb.module.css';

/**
 * Breadcrumb Navigation Component
 * 
 * Props:
 * - items: Array of { label, path?, icon? }
 *   - label (string): Display text
 *   - path (string, optional): Route to navigate to. If not provided, item is not clickable
 *   - icon (ReactNode, optional): Icon to show before label
 * 
 * Example:
 *   <Breadcrumb items={[
 *     { label: 'Hub', path: '/hub' },
 *     { label: 'Create Store', path: '/biz/create' },
 *     { label: 'Step 1' }
 *   ]} />
 */
export default function Breadcrumb({ items = [] }) {
  const navigate = useNavigate();
  const allItems = useMemo(
    () => [{ label: 'Home', path: '/hub', icon: <HomeOutlined />, isHome: true }, ...items],
    [items]
  );

  const handleClick = (path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <nav className={styles.breadcrumb} aria-label="Breadcrumb">
      <ol className={styles.breadcrumbList}>
        {allItems.map((item, idx) => {
          const isCurrent = idx === allItems.length - 1;
          const isMiddle = idx > 0 && idx < allItems.length - 2;
          const itemClassName = [
            styles.breadcrumbItem,
            item.isHome ? styles.homeItem : '',
            isMiddle ? styles.middleItem : '',
          ].filter(Boolean).join(' ');

          return (
            <li key={`${item.label}-${idx}`} className={itemClassName}>
              {idx > 0 && <span className={styles.separator}>/</span>}
              {item.path && !isCurrent ? (
              <button
                className={styles.breadcrumbLink}
                onClick={() => handleClick(item.path)}
                aria-label={item.isHome ? 'Home' : undefined}
              >
                {item.icon && <span className={styles.breadcrumbIcon}>{item.icon}</span>}
                <span className={styles.labelText}>{item.label}</span>
              </button>
            ) : (
              <span className={styles.breadcrumbCurrent}>
                {item.icon && <span className={styles.breadcrumbIcon}>{item.icon}</span>}
                <span className={styles.labelText}>{item.label}</span>
              </span>
            )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
